
import { Loader2, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash/debounce';
import StockService from '../Api_Module/Api_Services/StockService';
import { useCallback, useEffect, useRef, useState } from 'react';

const SearchBar = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [results, setResults] = useState<Array<{ symbol: string; name: string }>>([]);
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const currentRequestId = useRef(0);

    const handleNavigate = (val: string) => {
        setSearchTerm('');
        setResults([]);
        navigate(`/${val}/summary`);
    };

    const debouncedSearch = useCallback(
        debounce(async (value: string, requestId: number) => {
            setIsLoading(true);
            const q = value.trim();
            if (q.length < 1) {
                setResults([]);
                setIsLoading(false);
                return;
            }
            try {
                const res = await StockService.getStockSymbolsSearch(q);
                if (currentRequestId.current === requestId) {
                    const items: any[] = (res?.symbols || res?.data || res || []) as any[];
                    const mapped = (items || []).map((it: any) => ({
                        symbol: it.symbol || it.ticker || it.code || '',
                        name: it.name || it.companyName || it.label || '',
                    }));
                    setResults(mapped);
                }
            } catch (error) {
                if (currentRequestId.current === requestId) {
                    setResults([]);
                }
            } finally {
                if (currentRequestId.current === requestId) {
                    setIsLoading(false);
                }
            }
        }, 250),
        []
    );

    const handleSearch = useCallback((value: string) => {
        const newRequestId = Date.now();
        currentRequestId.current = newRequestId;
        debouncedSearch(value, newRequestId);
    }, [debouncedSearch]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.toLowerCase();
        setSearchTerm(value);
        if (value.trim() === '') {
            setResults([]);
            setIsLoading(false);
            currentRequestId.current = 0;
            return;
        }
        handleSearch(value);
    };
    
    // Focus the input with '/' shortcut
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement | null;
            const isTypingInInput = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
            if (!isTypingInInput && e.key === '/') {
                e.preventDefault();
                inputRef.current?.focus();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);

    const trending = ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'TSLA', 'META'];

    return (
        <div className="relative w-full">
            <input
                ref={inputRef}
                type="text"
                placeholder="Search for stocks (press '/' to focus)"
                className="w-full py-2 px-3 pl-10 pr-9 rounded-md bg-theme-secondary border border-theme-tertiary text-theme-primary placeholder-theme-muted focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                value={searchTerm}
                onChange={handleInputChange}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setTimeout(() => setIsFocused(false), 150)}
            />
            <Search className="absolute left-3 top-2.5 text-theme-muted" size={18} />
            {isLoading && <Loader2 className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-theme-muted" />}

            {(isFocused || results.length > 0) && (
                <div className="absolute left-0 right-0 w-full bg-theme-secondary rounded-lg shadow-lg mt-2 border border-theme-tertiary" style={{ zIndex: 99999 }}>
                    {isLoading && currentRequestId.current !== 0 && (
                        <div className="px-4 py-2 text-sm text-theme-muted">Searching...</div>
                    )}
                    {results.length > 0 && (
                        <ul className="mt-1 space-y-1 h-auto max-h-[220px] pr-2 overflow-y-auto custom-scrollbar">
                            {results.map((stock: any) => (
                                <li
                                    key={stock.symbol}
                                    className="flex items-center justify-between bg-theme-secondary hover:bg-theme-tertiary px-4 py-2 transition-colors cursor-pointer"
                                    onClick={() => handleNavigate(stock.symbol)}
                                >
                                    <span className="text-theme-primary font-semibold">{stock.symbol || stock.name}</span>
                                    { (stock.symbol && stock.name) && (
                                      <span className="text-theme-muted">- {stock.name}</span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                    {results.length === 0 && !searchTerm && !isLoading && (
                        <div className="px-4 py-3">
                            <p className="text-xs text-theme-muted mb-2">Try one of these trending tickers</p>
                            <div className="flex flex-wrap gap-2">
                                {trending.map(t => (
                                    <button
                                        key={t}
                                        className="text-xs rounded-full border border-theme-tertiary bg-theme-secondary hover:bg-theme-tertiary px-3 py-1 text-theme-primary"
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => handleNavigate(t)}
                                    >
                                        {t}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    {results.length === 0 && searchTerm && !isLoading && searchTerm.length > 1 && (
                        <div className="px-4 py-2 text-sm text-theme-muted">No results found</div>
                    )}
                </div>
            )}
        </div>
    );
};
export default SearchBar;